// ChatPaneV2 (issue #420). Hosts the existing ChatContent + BottomBarContent
// components, but drives them with the new v2 hooks (`useChatMessages`,
// `useSendMessage`, `useMarkRead`) instead of the legacy fetch logic.
//
// We deliberately do not delete or rewrite `ChatContent.tsx` and
// `BottomBarContent.tsx` — they still own the message rendering, attachment
// upload, and pin UX. This wrapper threads in messages from the cursor-
// paginated infinite query and stubs the legacy `socket.emit('chat:join')`
// the inner ChatContent issues on mount.
//
// The event router (#418) handles incoming `chat:message:new` events for
// the whole app, so this pane does not subscribe to the socket itself.

import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { useEffect, useMemo, useRef } from 'react';

import BottomBarContent from './BottomBarContent';
import ChatContent from './ChatContent';
import SupportThreadComposer from './SupportThreadComposer';
import { isAdminRole, readSession } from './messengerSession';

import { useChatMessages, useMarkRead } from 'src/queries/chat';
import { useChatSocket } from 'src/realtime/ChatSocketProvider';

import type { Chat, ChatMessage, TopicContext, TopicSource } from 'src/models/chat';

interface ChatPaneV2Props {
  supportScope?: 'legacy' | 'platform';
  chat: Chat;
  /**
   * Hide the message composer (e.g. for `JOB_BROADCAST` rooms where the
   * caller cannot reply). The legacy BottomBarContent always renders, so
   * we conditionally swap it out.
   */
  readOnly?: boolean;
  /** Event threads (#4005): show one sub-thread, or every message when absent. */
  messageFilter?: (message: ChatMessage) => boolean;
  /** Where replies start or continue; the server names the sub-thread. */
  sendContext?: TopicContext;
  /** The server's copy of a message this composer just sent. */
  onSent?: (message?: ChatMessage) => void;
  onOpenSource?: (message: ChatMessage, topic: { key: string; label: string; source?: TopicSource }) => void;
  /** Narrow side-panel layout. */
  compact?: boolean;
  showTopicLabels?: boolean;
  onReadMessages?: (messages: ChatMessage[]) => void;
  composerContext?: string;
  draftScope?: string;
  focusMessageId?: number;
}

/**
 * Flatten the infinite-query pages into a chronological list (oldest first)
 * the way the legacy `ChatContent` expects. Page 0 is newest, so we reverse
 * each page's `data` (which is also newest-first) before concatenating in
 * reverse page order.
 */
function flattenMessages(
  pages: Array<{ data: ChatMessage[] }> | undefined
): ChatMessage[] {
  if (!pages || pages.length === 0) return [];
  const ascending: ChatMessage[] = [];
  for (let i = pages.length - 1; i >= 0; i--) {
    const reversed = [...pages[i].data].reverse();
    ascending.push(...reversed);
  }
  return ascending;
}

/** Older pages a sub-thread view loads on its own while it has nothing to show. */
const AUTO_PAGE_LIMIT = 5;

export default function ChatPaneV2({
  chat, readOnly, supportScope = 'legacy', messageFilter, sendContext, onSent, onOpenSource, compact, showTopicLabels, onReadMessages, composerContext, focusMessageId, draftScope,
}: ChatPaneV2Props) {
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const { socket } = useChatSocket();
  const markRead = useMarkRead(supportScope);

  // Admin-only internal-note composer is rendered for SUPPORT threads. The
  // session role gate mirrors the SupportInbox guard — the backend remains
  // the authoritative gate; this just avoids baiting a non-admin into a 403.
  const session = useMemo(() => readSession(), []);
  const showSupportComposer =
    chat.type === 'SUPPORT' && isAdminRole(session?.roleName);

  const {
    data,
    isFetching,
    isError,
    fetchNextPage,
    refetch,
    hasNextPage,
    isFetchingNextPage,
  } = useChatMessages(chat.id, undefined, supportScope);
  const allMessages = useMemo(() => flattenMessages(data?.pages), [data?.pages]);
  const messages = useMemo(
    () => (messageFilter ? allMessages.filter(messageFilter) : allMessages),
    [allMessages, messageFilter]
  );
  // A sub-thread whose messages are on older pages loads a few of them on its own. A sub-thread with
  // no messages yet would otherwise page through the whole thread, so this stops at the oldest page,
  // after AUTO_PAGE_LIMIT pages for the same view, or on a failed load. Manual load-older still works.
  // The count follows the filter's identity, so callers pass a memoized filter. The page count re-runs
  // the check after each page lands, even when the in-flight flag never reaches a render.
  const pageCount = data?.pages.length ?? 0;
  const autoPaged = useRef<{ chatId: number; filter?: ChatPaneV2Props['messageFilter']; count: number }>({
    chatId: chat.id, count: 0,
  });
  useEffect(() => {
    if (autoPaged.current.chatId !== chat.id || autoPaged.current.filter !== messageFilter) {
      autoPaged.current = { chatId: chat.id, filter: messageFilter, count: 0 };
    }
    if (sendContext?.ticket || focusMessageId || !messageFilter || messages.length > 0 || !hasNextPage || isFetchingNextPage || isError) return;
    if (autoPaged.current.count >= AUTO_PAGE_LIMIT) return;
    autoPaged.current.count += 1;
    // Never cancel the refetch that brings in a message just sent.
    void fetchNextPage({ cancelRefetch: false });
  }, [chat.id, messageFilter, messages.length, pageCount, hasNextPage, isFetchingNextPage, isError, fetchNextPage, focusMessageId, sendContext?.ticket]);

  // Mark the latest message as read on mount of a chat. Debounce avoids a
  // tight loop when a flurry of socket pushes lands in quick succession.
  useEffect(() => {
    if (onReadMessages) { onReadMessages(messages); return; }
    const last = allMessages[allMessages.length - 1];
    if (!last) return;
    const handle = window.setTimeout(() => {
      markRead.mutate({ chatId: chat.id, messageId: last.id });
    }, 1000);
    return () => window.clearTimeout(handle);
    // Intentionally subscribed only to `chat.id` and `messages.length` to
    // avoid retriggering on every cache mutation; `markRead` is stable
    // across renders inside react-query.
  }, [chat.id, allMessages.length, messages, onReadMessages]);

  // MSG-06: locate a historical search hit, not merely the newest page.
  useEffect(() => {
    if (!focusMessageId) return;
    const node = messagesEndRef.current;
    const found = node?.querySelector<HTMLElement>(`[data-message-id="${focusMessageId}"]`);
    if (found && node) {
      node.scrollTop += found.getBoundingClientRect().top - node.getBoundingClientRect().top;
    } else if (!allMessages.some(message => message.id === focusMessageId) && hasNextPage && !isFetching && !isError) {
      void fetchNextPage({ cancelRefetch: false });
    }
  }, [focusMessageId, allMessages, hasNextPage, isFetching, isError, fetchNextPage]);

  const scrollToBottom = () => {
    const node = messagesEndRef.current;
    if (node) node.scrollTo({ top: node.scrollHeight, behavior: 'smooth' });
  };

  // The legacy ChatContent expects an `setMessages` setter so it can append
  // socket messages locally. With the v2 router that update flows through
  // the React Query cache, so we hand it a no-op setter and rely on
  // `messages` re-rendering when the cache changes.
  const noopSetMessages = (
    _next:
      | ChatMessage[]
      | ((prev: ChatMessage[]) => ChatMessage[])
  ): void => {
    /* no-op: the v2 cache is the source of truth */
  };

  if (isFetching && allMessages.length === 0) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <>
      <Box
        flex={1}
        sx={{ minHeight: 0, overflow: 'auto', scrollbarWidth: 'thin', ...(focusMessageId ? { [`& [data-message-id="${focusMessageId}"]`]: { bgcolor: 'action.selected', outline: '1px solid', outlineColor: 'primary.main' } } : {}) }}
        ref={messagesEndRef}
      >
        {focusMessageId && !hasNextPage && !isFetching && !allMessages.some(message => message.id === focusMessageId) && <Typography role="status" p={2}>The matching message is no longer available.</Typography>}
        {isError && <Box p={2}><Typography role="alert">Messages could not load.</Typography><Button onClick={() => refetch()}>Retry</Button></Box>}
        {hasNextPage && !sendContext?.ticket && (
          <Box display="flex" justifyContent="center" p={2}>
            <Button
              variant="contained"
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage}
            >
              {isFetchingNextPage ? 'Loading…' : 'View Previous Messages'}
            </Button>
          </Box>
        )}
        {/* Retain message rendering; the cursor query is the sole history transport. */}
        {socket && (
          <ChatContent
            chat={chat}
            messages={messages}
            setMessages={noopSetMessages}
            scrollToBottom={scrollToBottom}
            socket={socket}
            supportScope={supportScope}
            managedMessages
            compact={compact}
            showTopicLabels={showTopicLabels}
            onOpenSource={onOpenSource}
          />
        )}
      </Box>
      {!readOnly && composerContext && <Typography variant="caption" sx={{ px: 2, pt: 1, color: 'text.secondary', textTransform: 'none' }}>{composerContext}</Typography>}
      {!readOnly && socket && showSupportComposer && (
        <SupportThreadComposer
          chat={chat}
          messages={messages}
          setMessages={noopSetMessages}
          scrollToBottom={scrollToBottom}
          socket={socket}
          supportScope={supportScope}
          canonicalTransport
        />
      )}
      {!readOnly && socket && !showSupportComposer && (
        <BottomBarContent
          chat={chat}
          messages={messages}
          setMessages={noopSetMessages}
          scrollToBottom={scrollToBottom}
          socket={socket}
          supportScope={supportScope}
          canonicalTransport
          draftScope={draftScope}
          sendContext={sendContext}
          onSent={onSent}
          compact={compact}
        />
      )}
    </>
  );
}
